import _ from 'lodash';
import { Search } from 'lucide-react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ChainlitContext, IThread } from '@chainlit/react-client';

import { Loader } from '@/components/Loader';
import { Button } from '@/components/ui/button';
import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
} from '@/components/ui/command';
import { DialogTitle } from '@/components/ui/dialog';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from '@/components/ui/tooltip';
import { Translator } from 'components/i18n';

import { useHotkey } from '@/hooks/useHotkey';

import { Kbd } from '../Kbd';

interface SearchChatsProps {
    showText?: boolean;
    hotkey?: string;
}

export default function SearchChats({
    showText = true,
    hotkey
}: SearchChatsProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [threads, setThreads] = useState<IThread[]>([]);
    const [loading, setLoading] = useState(false);

    const apiClient = useContext(ChainlitContext);

    // Fetch all threads (backend search doesn't work, so we filter locally)
    const fetchThreads = useMemo(
        () =>
            _.debounce(async () => {
                setLoading(true);
                try {
                    const { data } = await apiClient.listThreads(
                        { first: 20, cursor: undefined },
                        { isDeepIntelligence: false }
                    );
                    setThreads(data || []);
                } catch (error) {
                    toast.error('Error fetching threads: ' + error);
                } finally {
                    setLoading(false);
                }
            }, 300),
        [apiClient]
    );

    // Filter threads based on search query and group by month and year
    const groupedThreads = useMemo(() => {
        const filtered = searchQuery
            ? threads.filter((thread) =>
                  (thread.name || 'Untitled Conversation')
                      .toLowerCase()
                      .includes(searchQuery.toLowerCase())
              )
            : threads;

        return _.groupBy(filtered, (thread) => {
            const date = new Date(thread.createdAt);
            return `${date.toLocaleString('default', {
                month: 'long'
            })} ${date.getFullYear()}`;
        });
    }, [threads, searchQuery]);

    useHotkey(hotkey, () => setOpen((open) => !open));

    useEffect(() => {
        if (open) {
            fetchThreads();
        }
        return () => {
            fetchThreads.cancel();
        };
    }, [fetchThreads, open]);

    const searchField = (
        <button
            id="search-chats-button"
            type="button"
            onClick={() => setOpen(!open)}
            className="group/search flex items-center gap-3 hover:bg-background px-3 border border-transparent hover:border-border rounded-xl w-full h-10 text-(length:--fs-title) text-foreground text-left transition-colors"
        >
            <Search strokeWidth={1.5} className="size-5! shrink-0" />
            <span className="flex-1 min-w-0 truncate">Search...</span>
            {hotkey && (
                <Kbd className="hidden group-hover/search:inline-flex shrink-0">
                    {hotkey}
                </Kbd>
            )}
        </button>
    );

    const searchButton = showText ? (
        searchField
    ) : (
        <Button
            id="search-chats-button"
            onClick={() => setOpen(!open)}
            variant="bare"
            className="justify-center p-2 rounded-lg w-full h-10 font-normal text-foreground transition-all duration-200"
        >
            <Search strokeWidth={1.5} className="size-5! shrink-0" />
        </Button>
    );

    const searchButtonWithTooltip = !showText ? (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>{searchButton}</TooltipTrigger>
                <TooltipContent side="right">Search chats</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    ) : (
        searchButton
    );

    return (
        <>
            <div className="flex justify-center">{searchButtonWithTooltip}</div>

            <CommandDialog
                open={open}
                onOpenChange={setOpen}
                shouldFilter={false}
            >
                <DialogTitle className="sr-only">
                    {t('threadHistory.sidebar.filters.search')}
                </DialogTitle>
                <div className="flex items-center px-4.5 border-b">
                    <Search
                        strokeWidth={1.5}
                        className="mr-3 text-muted-foreground size-5 shrink-0"
                    />
                    <input
                        autoFocus
                        className="flex bg-transparent disabled:opacity-50 py-3 outline-hidden w-full h-14 placeholder:text-muted-foreground text-(length:--fs-title) disabled:cursor-not-allowed"
                        placeholder={t(
                            'threadHistory.sidebar.filters.placeholder'
                        )}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <CommandList className="p-2 h-[340px] overflow-y-auto">
                    {loading ? (
                        <CommandEmpty className="flex justify-center items-center p-4">
                            <Loader />
                        </CommandEmpty>
                    ) : Object.keys(groupedThreads).length === 0 ? (
                        <CommandEmpty>
                            <Translator path="threadHistory.sidebar.empty" />
                        </CommandEmpty>
                    ) : (
                        Object.entries(groupedThreads).map(
                            ([monthYear, monthThreads]) => (
                                <CommandGroup
                                    key={monthYear}
                                    heading={monthYear}
                                    className="p-0 **:[[cmdk-group-heading]]:px-2.5 **:[[cmdk-group-heading]]:pt-3 **:[[cmdk-group-heading]]:pb-1.5 **:[[cmdk-group-heading]]:font-normal"
                                >
                                    {monthThreads.map((thread) => (
                                        <CommandItem
                                            className="px-2.5 py-2.5 rounded-lg text-(length:--fs-title) cursor-pointer"
                                            key={thread.id}
                                            value={thread.id}
                                            onSelect={() => {
                                                setOpen(false);
                                                navigate(
                                                    `/thread/${thread.id}`
                                                );
                                            }}
                                        >
                                            <div className="line-clamp-2">
                                                {thread.name ||
                                                    'Untitled Conversation'}
                                            </div>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            )
                        )
                    )}
                </CommandList>
            </CommandDialog>
        </>
    );
}
